import { Check, Clock, MapPin, Package, Truck, User } from "lucide-react";

type TimelineStep = {
  label: string;
  detail?: string;
  status: "done" | "current" | "upcoming";
  icon: "order" | "driver" | "pickup" | "delivery" | "delivered";
};

type Props = {
  deliveryRequest: {
    status: string;
    acceptedByUserId?: string | null;
    stops?: { sellerName: string; status?: string; pickedUpAt?: string | null }[];
    completedAt?: string | null;
    createdAt?: string | null;
    deliverByDate?: string | null;
  } | null;
  driverName?: string | null;
};

const ICON_MAP = {
  order: Package,
  driver: User,
  pickup: MapPin,
  delivery: Truck,
  delivered: Check,
};

function formatDate(d: string | null | undefined) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return null;
  }
}

export function DeliveryTimeline({ deliveryRequest, driverName }: Props) {
  if (!deliveryRequest) return null;

  const req = deliveryRequest;

  // A cancelled/expired delivery isn't "waiting for a driver" — show that
  // clearly instead of the normal in-progress steps.
  if (req.status === "cancelled" || req.status === "expired") {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        {req.status === "cancelled"
          ? "This delivery was cancelled."
          : "This delivery request expired without a driver."}
      </div>
    );
  }

  const steps: TimelineStep[] = [];

  // Step 1: Request created
  steps.push({
    label: "Delivery requested",
    detail: formatDate(req.createdAt) ?? undefined,
    status: "done",
    icon: "order",
  });

  // Step 2: Driver assigned
  const hasDriver = req.status !== "open" && req.status !== "expired" && req.status !== "cancelled";
  steps.push({
    label: hasDriver ? `Driver assigned${driverName ? ` — ${driverName}` : ""}` : "Waiting for driver",
    detail: hasDriver ? undefined : "A community member will accept soon",
    status: hasDriver ? "done" : req.status === "open" ? "current" : "upcoming",
    icon: "driver",
  });

  // Step 3: Pickups
  const stops = req.stops ?? [];
  const allPickedUp = stops.length > 0 && stops.every((s) => s.status === "picked_up");
  const anyPickedUp = stops.some((s) => s.status === "picked_up");
  const pickedUpCount = stops.filter((s) => s.status === "picked_up").length;

  if (stops.length > 0) {
    steps.push({
      label: allPickedUp
        ? `All ${stops.length} pickup${stops.length === 1 ? "" : "s"} complete`
        : anyPickedUp
          ? `${pickedUpCount} of ${stops.length} pickups done`
          : `${stops.length} seller pickup${stops.length === 1 ? "" : "s"}`,
      detail: stops.map((s) => `${s.status === "picked_up" ? "✓" : "○"} ${s.sellerName}`).join(" · "),
      status: allPickedUp ? "done" : anyPickedUp ? "current" : hasDriver ? "upcoming" : "upcoming",
      icon: "pickup",
    });
  }

  // Step 4: Out for delivery
  const isOutForDelivery = req.status === "in_progress" && allPickedUp;
  steps.push({
    label: "Out for delivery",
    status: isOutForDelivery ? "current" : req.status === "completed" ? "done" : "upcoming",
    icon: "delivery",
  });

  // Step 5: Delivered
  steps.push({
    label: "Delivered",
    detail: req.status === "completed" && req.completedAt ? formatDate(req.completedAt) ?? undefined : undefined,
    status: req.status === "completed" ? "done" : "upcoming",
    icon: "delivered",
  });

  // Enforce monotonic progress: a later step must never render "done" while
  // an earlier one isn't. A completed delivery means every step happened;
  // otherwise, once we hit the first not-done step, nothing after it can be
  // "done". Guards against out-of-band states (e.g. an order advanced to
  // delivered without a real pickup) showing "Delivered ✓" over an
  // unfinished pickup.
  if (req.status === "completed") {
    for (const s of steps) s.status = "done";
  } else {
    let sawIncomplete = false;
    for (const s of steps) {
      if (s.status !== "done") sawIncomplete = true;
      else if (sawIncomplete) s.status = "upcoming";
    }
  }

  return (
    <div className="space-y-0">
      {steps.map((step, i) => {
        const Icon = ICON_MAP[step.icon];
        const isLast = i === steps.length - 1;
        return (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full ${
                  step.status === "done"
                    ? "bg-primary text-primary-foreground"
                    : step.status === "current"
                      ? "border-2 border-primary bg-primary/10 text-primary"
                      : "border border-border bg-muted text-muted-foreground"
                }`}
              >
                {step.status === "done" ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              {!isLast && (
                <div className={`h-8 w-0.5 ${step.status === "done" ? "bg-primary" : "bg-border"}`} />
              )}
            </div>
            <div className="pb-6">
              <p className={`text-sm font-medium ${step.status === "current" ? "text-primary" : step.status === "upcoming" ? "text-muted-foreground" : ""}`}>
                {step.label}
              </p>
              {step.detail && (
                <p className="mt-0.5 text-[11px] text-muted-foreground">{step.detail}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
